import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';

import { TechnicalLogMapper } from '../../models/technical-log.mapper';
import type { TechnicalLogEntry, TechnicalLogRecord } from '../../models/technical-log-entry.model';
import {
  isTechnicalLogFailure,
  TECHNICAL_LOG_MAX_LENGTH,
  TECHNICAL_LOG_REPORT_WINDOW_MS,
  TECHNICAL_LOG_RETENTION_MS,
} from '../../models/technical-log-entry.model';
import {
  TECHNICAL_LOG_STORE_PROVIDER,
  USER_ACTIVITY_PROVIDER,
} from '../../tokens/telemetry.tokens';
import type { ITechnicalLogHandler } from './i-technical-log.handler';

/**
 * Ogni quanto il buffer scende nell'archivio. Scrivere a ogni riga significherebbe una transazione per
 * ogni chiamata di rete; aspettare troppo significherebbe perdere l'ultimo minuto — che e' quello che
 * spiega il guasto — se la scheda si chiude.
 */
const FLUSH_INTERVAL_MS = 5000;

/** Oltre questa misura il buffer scende subito, senza aspettare il giro. */
const FLUSH_BATCH_SIZE = 50;

/**
 * Ogni quanto si butta cio' che e' scaduto mentre la pagina resta aperta. Con una vita di mezz'ora la
 * sola pulizia all'avvio non basta: una scheda aperta per un pomeriggio terrebbe ore di righe.
 */
const PRUNE_INTERVAL_MS = 60 * 1000;

/**
 * Il diario tecnico. Tiene in memoria le ultime righe e le fa scendere nell'archivio a intervalli, cosi'
 * una raffica di errori non paga una transazione ciascuno.
 *
 * Tiene soltanto cio' che e' andato storto — errori e chiamate di rete fallite, vedi
 * {@link isTechnicalLogFailure}: la scelta e' qui e non in chi registra, cosi' una riga di troppo non
 * arriva all'archivio da nessuna strada.
 */
@Injectable()
export class TechnicalLogHandler implements ITechnicalLogHandler {
  private readonly store = inject(TECHNICAL_LOG_STORE_PROVIDER);
  private readonly userActivity = inject(USER_ACTIVITY_PROVIDER);
  private readonly destroyRef = inject(DestroyRef);

  private buffer: TechnicalLogEntry[] = [];

  private started = false;

  record(record: TechnicalLogRecord): void {
    if (!isTechnicalLogFailure(record)) return;

    this.buffer.push({ at: Date.now(), ...record });

    if (this.buffer.length >= FLUSH_BATCH_SIZE) void this.flush();
  }

  start(): void {
    // Idempotente: l'avvio e' uno solo, ma un secondo giro raddoppierebbe ogni riga registrata.
    if (this.started) return;
    this.started = true;

    // Una riga vive quanto la finestra di una segnalazione: oltre, resterebbe solo come traccia su un
    // computer che puo' essere di qualcun altro. Si pulisce all'avvio e poi a intervalli.
    this.prune();
    interval(PRUNE_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.prune());

    interval(FLUSH_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.flush());

    // L'ultimo istante utile: una scheda che passa in secondo piano puo' non tornare mai, e con lei se
    // ne andrebbe il minuto che precede la segnalazione.
    this.userActivity.pageHidden$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.flush());
  }

  async collectRecent(): Promise<string> {
    // Prima si mette al sicuro cio' che e' ancora in memoria, e si aspetta: le righe piu' interessanti
    // di una segnalazione sono quelle dei secondi appena passati, e leggere senza aspettare la scrittura
    // le lascerebbe fuori proprio dalla segnalazione che le riguarda.
    await this.flush();

    const since = Date.now() - TECHNICAL_LOG_REPORT_WINDOW_MS;
    // Si filtra anche in lettura: le righe scritte dalla versione precedente — gesti, navigazioni,
    // chiamate riuscite — possono essere ancora nell'archivio finche' la pulizia non passa.
    const entries = (await this.store.readSince(since)).filter((entry) =>
      isTechnicalLogFailure(TechnicalLogMapper.toDomain(entry)),
    );

    return TechnicalLogMapper.toJsonWithin(entries, TECHNICAL_LOG_MAX_LENGTH);
  }

  private prune(): void {
    void this.store.deleteBefore(Date.now() - TECHNICAL_LOG_RETENTION_MS);
  }

  private flush(): Promise<void> {
    if (this.buffer.length === 0) return Promise.resolve();

    const pending = this.buffer;
    this.buffer = [];

    return this.store.append(pending.map((entry) => TechnicalLogMapper.toDto(entry)));
  }
}
